import { useState, ChangeEvent, KeyboardEvent, useRef, useMemo, useCallback, useEffect } from 'react';
import { useOnClickOutside } from '@vfit/shared/hooks';
import { ISearchSelectHook } from './searchSelect.models';

export const useSearchSelect = ({
  items,
  value,
  ignoreInnerErrors,
  isDisabled,
  searchable,
  setError,
  setValue,
  clearErrors,
  trigger,
}: ISearchSelectHook) => {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [isListFocused, setIsListFocused] = useState<boolean>(false);

  const wrapperRef = useRef(null);
  useOnClickOutside(wrapperRef.current, () => {
    setShowOptions(false);
    setIsListFocused(false);
  });

  const toggleShowOptions = (): void => {
    if (!isDisabled) {
      setShowOptions((prev) => !prev);
      setIsListFocused(false);
    }
  };

  const openOptions = (): void => {
    if (!isDisabled) {
      setShowOptions(true);
    }
  };

  const getFilteredItems = useCallback(
    (text: string) => {
      if (!text) {
        return items;
      }

      const t = text.toLowerCase();

      return items
        ?.filter((s) => s.toLowerCase().indexOf(t) !== -1)
        .sort((first, second) => {
          const f = first.toLowerCase();
          const s = second.toLowerCase();

          if (f.startsWith(t) && !s.startsWith(t)) {
            return -1;
          }
          if (!f.startsWith(t) && s.startsWith(t)) {
            return 1;
          }
          if (f < s) {
            return -1;
          }
          if (f > s) {
            return 1;
          }
          return 0;
        });
    },
    [items]
  );

  const filteredItems = useMemo(
    () => (searchable ? getFilteredItems(value) : items),
    [getFilteredItems, items, searchable, value]
  );

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredItems, showOptions]);

  const showSuggestions = useMemo(
    () => filteredItems?.length > 0 && showOptions && !isDisabled,
    [filteredItems?.length, isDisabled, showOptions]
  );

  const getError = (v: string) =>
    ignoreInnerErrors
      ? ''
      : (v && getFilteredItems(v).length === 0 ? 'Nessuna opzione disponibile' : '') ||
        (v && !getFilteredItems(v).includes(v) ? 'Selezionare una voce corretta' : '');

  const triggerError = (v: string) => {
    const error = getError(v);
    if (error) {
      setError({ type: 'custom', message: error });
    } else {
      trigger();
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    setValue(e.target.value);
    setIsListFocused(false);
    if (!e.target.value) {
      setShowOptions(false);
    } else {
      setShowOptions(true);
      triggerError(e.target.value);
    }
  };

  const onSelect = (v: string): void => {
    clearErrors();
    setValue(v);
    setShowOptions(false);
    setIsListFocused(false);
  };

  const handleWrapperKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const isInput = (e.target as HTMLElement).tagName === 'INPUT';
    const isButton = (e.target as HTMLElement).tagName === 'BUTTON';

    if (e.code === 'Backspace' && isInput) {
      onSelect('');
      triggerError('');
    }

    if (!showSuggestions) {
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && (isInput || isButton)) {
        openOptions();
        e.preventDefault();
      }
      return;
    }

    if (e.key === 'Tab') {
      if (isListFocused) {
        // Exit list and let tab propagate natively
        setIsListFocused(false);
        setShowOptions(false);
      } else if (isButton && !e.shiftKey) {
        // Move focus to list if tab is pressed on the button forward
        e.preventDefault();
        setIsListFocused(true);
        if (filteredItems.length > 0) {
          setHighlightedIndex(0);
        }
      } else {
        // Let it tab natively from input to button, or from button back to input (shift+tab)
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isListFocused) {
        setIsListFocused(true);
        setHighlightedIndex(0);
      } else {
        setHighlightedIndex((prev) => (prev < filteredItems.length - 1 ? prev + 1 : prev));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isListFocused) {
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (isListFocused && highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
        e.preventDefault();
        onSelect(filteredItems[highlightedIndex]);
      } else if (e.key === ' ' && isButton) {
        // Native space on button opens/closes it
        e.preventDefault();
        toggleShowOptions();
      }
    } else if (e.key === 'Escape') {
      setIsListFocused(false);
      setShowOptions(false);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // We only trigger error on blur if the list isn't focused
    // A related issue: when clicking outside, blur usually fires, but if we keep actual DOM focus on the input, we are fine.
    triggerError(e.target.value);
  };

  return {
    showSuggestions,
    filteredItems,
    wrapperRef,
    toggleShowOptions,
    openOptions,
    onSelect,
    onChange,
    handleWrapperKeyDown,
    handleBlur,
    highlightedIndex,
    isListFocused
  };
};

